import { useNavigate } from 'react-router';
import { MessageSquare, Plus } from 'lucide-react';
import { cn } from 'helpers/cn';
import { SURVEY_TYPES } from 'definitions';
import { TEMPLATES } from '@/state/surveys/useSurveyBuilder';
import { Button } from '@/components/ui/button';

/**
 * First-run screen for the survey library.
 *
 * The library's own empty table said "No surveys yet" and left the reader to work
 * out what a survey is from a filter strip and six column headings. Pipelines
 * answers that question on its own screen, so surveys does too: what the thing is,
 * which instrument to reach for, and what the four builder steps will ask.
 *
 * It replaces the toolbar rather than sitting under it, because with nothing to
 * filter or search the strip is six controls that all do nothing. The one control
 * worth keeping is the one that ends this state.
 */

/** The builder's four steps, named here so the wizard holds no surprises. */
const STEPS = [
  'Pick a survey type and name it for yourself. Visitors never see the internal name.',
  'Edit the scored question and its optional follow-up.',
  'Match the widget to your site: accent colour, corner radius, and where it sits.',
  'Choose when it fires: a delay, a scroll depth, exit intent, or one of your own events.',
];

export const SurveysEmptyState = () => {
  const navigate = useNavigate();

  return (
    <main className="flex flex-col gap-[14px]">
      <section className="bg-panel border-line rounded-panel border px-4 py-10 sm:px-6 sm:py-12" data-first-run>
        <div className="mx-auto max-w-2xl text-center">
          <MessageSquare className="text-dim mx-auto size-10" strokeWidth={1.25} />
          <h1 className="text-foreground m-0 mt-4 text-title font-medium tracking-tight">No surveys yet</h1>
          <p className="supporting mx-auto mt-2 max-w-xl">
            A survey is a small prompt shown on your own site that asks a visitor a few short questions: a score, a
            choice, or something in their own words. Scores roll up into a number you can track over time, and the
            written answers can be classified by sentiment, if you switch that on, so a few hundred of them stay
            readable.
          </p>

          <Button onClick={() => navigate('/surveys/new')} className="mt-5 h-9 rounded-lg">
            <Plus className="size-4" />
            New survey
          </Button>
        </div>
      </section>

      <section className="bg-panel border-line rounded-panel border">
        <div className="border-line flex items-center justify-between border-b px-3 py-2.5">
          <span className="micro-label">Survey types</span>
          <span className="micro-label tracking-normal">{SURVEY_TYPES.length}</span>
        </div>
        {/*
          Same blurbs as the builder's first step, from TEMPLATES — the choice is
          explained once, so the library and the wizard cannot describe the same
          types differently.
        */}
        <div className="grid gap-2.5 p-3 sm:grid-cols-2">
          {SURVEY_TYPES.map((type) => (
            <div key={type} className="border-line bg-panel2 rounded-card border p-3.5">
              <p className="text-foreground m-0 text-body font-medium">{type}</p>
              <p className="micro-label mt-1">{TEMPLATES[type].name}</p>
              <p className="supporting m-0 mt-2">{TEMPLATES[type].blurb}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-panel border-line rounded-panel border">
        <div className="border-line border-b px-3 py-2.5">
          <span className="micro-label">What the builder asks</span>
        </div>
        <ol className="m-0 flex list-none flex-col gap-2.5 p-3">
          {STEPS.map((step, index) => (
            <li key={step} className="flex items-start gap-2.5">
              <span
                className={cn(
                  'border-line text-dim num flex size-5 shrink-0 items-center justify-center',
                  'rounded-full border text-nano',
                )}
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <p className="supporting m-0">{step}</p>
            </li>
          ))}
        </ol>
        <div className="border-line border-t px-3 py-2.5">
          {/* Starting publishes the website (PER-332), so a started survey is live at once;
              targeting then decides who is asked. */}
          <p className="supporting m-0">
            Starting a survey puts it on the website straight away. Its targeting decides who is asked.
          </p>
        </div>
      </section>
    </main>
  );
};
